import {
  DeveloperAuthorContribution,
  DeveloperCatalogDto,
  DeveloperCommandCallerInfo,
  DeveloperCommandInfo,
  DeveloperControllerActionInfo,
  DeveloperControllerInfo,
  DeveloperExternalServiceInfo,
  DeveloperJobInfo,
} from '@6grain/masofa-monolith-api-client/common';

export type CatalogAuthor = DeveloperAuthorContribution;
export type CatalogExternalService = DeveloperExternalServiceInfo;

/** Appsettings / IOptions section shown next to catalog commands and jobs. */
export interface CatalogConfigurationSection {
  section?: string | null;
  optionsType?: string | null;
  source?: string | null;
  currentConfigJson?: string | null;
}

export type CatalogCommand = DeveloperCommandInfo & {
  configurationSections?: CatalogConfigurationSection[] | null;
};
export type CatalogJob = DeveloperJobInfo & {
  configurationSections?: CatalogConfigurationSection[] | null;
  processMigrationStatus?: JobProcessMigrationStatus | null;
};
export type CatalogController = DeveloperControllerInfo;
export type CatalogControllerAction = DeveloperControllerActionInfo;
export type CatalogDto = Omit<DeveloperCatalogDto, 'commands' | 'jobs' | 'controllers'> & {
  commands?: CatalogCommand[] | null;
  jobs?: CatalogJob[] | null;
  controllers?: CatalogController[] | null;
};

/** MediatR / CLI request kinds exposed by the code catalog. */
export type CatalogMediatorKind = 'Command' | 'Query' | 'Request' | 'CliCommand';

export type JobProcessMigrationStatus = 'AssignmentTransport' | 'Infrastructure' | 'LegacyCron';

const INFRASTRUCTURE_JOB_NAMES = new Set([
  'createpartitionjob',
  'healthcheckjob',
  'satellitestaleworkwatchdogjob',
  'processdueusernotificationsjob',
]);

/** Classifies a Quartz job type name the same way as JobProcessMigrationClassifier. */
export function classifyJobProcessMigrationStatus(
  jobTypeName?: string | null,
): JobProcessMigrationStatus {
  const name = jobTypeName?.trim() ?? '';
  if (!name) {
    return 'LegacyCron';
  }
  if (
    name.toLowerCase().endsWith('assignmentjob') ||
    name.toLowerCase() === 'satelliteprocessjob' ||
    name.toLowerCase() === 'processsubscriptionquartzjob'
  ) {
    return 'AssignmentTransport';
  }
  if (INFRASTRUCTURE_JOB_NAMES.has(name.toLowerCase())) {
    return 'Infrastructure';
  }
  return 'LegacyCron';
}

export function resolveJobProcessMigrationStatus(
  job: Pick<CatalogJob, 'name' | 'processMigrationStatus'> | null | undefined,
): JobProcessMigrationStatus {
  const stamped = job?.processMigrationStatus;
  if (
    stamped === 'AssignmentTransport' ||
    stamped === 'Infrastructure' ||
    stamped === 'LegacyCron'
  ) {
    return stamped;
  }
  return classifyJobProcessMigrationStatus(job?.name);
}

export function isLegacyCronJob(
  job: Pick<CatalogJob, 'name' | 'processMigrationStatus'> | null | undefined,
): boolean {
  return resolveJobProcessMigrationStatus(job) === 'LegacyCron';
}

export type CatalogAuthorParticipationKind = CatalogMediatorKind | 'Job' | 'Controller';

export interface CatalogAuthorParticipation {
  itemKind: CatalogAuthorParticipationKind;
  name: string;
  group: string;
  percent: number;
  lines: number;
}

export interface CatalogAuthorProfile {
  name: string;
  initials: string;
  itemCount: number;
  commandCount: number;
  jobCount: number;
  controllerCount: number;
  avgPercent: number;
  participations: CatalogAuthorParticipation[];
}

export interface CatalogServiceConsumer {
  itemKind: CatalogAuthorParticipationKind;
  name: string;
  group: string;
  entryPoint?: string | null;
  kind?: string | null;
  namingIssues?: string[] | null;
  namingCompliant?: boolean | null;
}

export interface CatalogServiceProfile {
  name: string;
  project: string;
  description: string;
  descriptions: Record<string, string>;
  itemCount: number;
  commandCount: number;
  jobCount: number;
  namingIssues: string[];
  namingCompliant: boolean;
  consumers: CatalogServiceConsumer[];
}

export const CATALOG_MEDIATOR_KINDS: readonly CatalogMediatorKind[] = [
  'Command',
  'Query',
  'Request',
  'CliCommand',
] as const;

const ARCHIVE_GROUP_PREFIX = 'Archive/';

export function isArchiveGroup(group: string | null | undefined): boolean {
  return !!group && (group === 'Archive' || group.startsWith(ARCHIVE_GROUP_PREFIX));
}

export function catalogGroupLabel(group: string | null | undefined): string {
  if (!group) {
    return 'Other';
  }
  if (group.startsWith(ARCHIVE_GROUP_PREFIX)) {
    return `Archive · ${group.slice(ARCHIVE_GROUP_PREFIX.length)}`;
  }
  return group;
}

export function compareCatalogGroups(left: string, right: string): number {
  const leftArchive = isArchiveGroup(left);
  const rightArchive = isArchiveGroup(right);
  if (leftArchive !== rightArchive) {
    return leftArchive ? 1 : -1;
  }
  return left.localeCompare(right);
}

export interface CatalogItemGroup<T> {
  group: string;
  items: T[];
}

export function groupByCatalogGroup<T extends { group?: string | null }>(
  items: T[] | null | undefined,
  compareItems?: (left: T, right: T) => number,
): CatalogItemGroup<T>[] {
  const map = new Map<string, T[]>();
  for (const item of items ?? []) {
    const key = item.group || 'Other';
    const bucket = map.get(key);
    if (bucket) {
      bucket.push(item);
    } else {
      map.set(key, [item]);
    }
  }
  return [...map.entries()]
    .sort(([left], [right]) => compareCatalogGroups(left, right))
    .map(([group, groupItems]) => ({
      group,
      items: compareItems ? [...groupItems].sort(compareItems) : groupItems,
    }));
}

export function compareCatalogAuthorParticipations(
  left: CatalogAuthorParticipation,
  right: CatalogAuthorParticipation,
): number {
  return compareCatalogGroups(left.group, right.group)
    || right.percent - left.percent
    || left.itemKind.localeCompare(right.itemKind)
    || left.name.localeCompare(right.name);
}

export function groupCatalogAuthorParticipations(
  items: CatalogAuthorParticipation[] | null | undefined,
): CatalogItemGroup<CatalogAuthorParticipation>[] {
  return groupByCatalogGroup(items, (left, right) =>
    right.percent - left.percent
    || left.itemKind.localeCompare(right.itemKind)
    || left.name.localeCompare(right.name),
  );
}

export function catalogArchiveGroup(group: string | null | undefined): string {
  const value = group || 'Other';
  if (isArchiveGroup(value)) {
    return value;
  }
  return `${ARCHIVE_GROUP_PREFIX}${value}`;
}

export function isCatalogItemObsolete(item: {
  obsolete?: boolean | null;
} | null | undefined): boolean {
  return !!item?.obsolete;
}

export function catalogKindLabel(kind: string | null | undefined): string {
  switch (kind) {
    case 'Query':
      return 'Queries';
    case 'Request':
      return 'Requests';
    case 'Command':
      return 'Commands';
    case 'CliCommand':
      return 'DevopsUtil CLI';
    case 'Job':
      return 'Jobs';
    case 'Controller':
      return 'Controllers';
    default:
      return kind || 'Other';
  }
}

export function catalogKindSeverity(
  kind: string | null | undefined,
): 'success' | 'info' | 'warn' | 'secondary' | 'contrast' | 'danger' {
  switch (kind) {
    case 'Query':
      return 'success';
    case 'Request':
      return 'warn';
    case 'Command':
      return 'info';
    case 'CliCommand':
      return 'danger';
    case 'Job':
      return 'contrast';
    case 'Controller':
      return 'secondary';
    default:
      return 'secondary';
  }
}

export interface CatalogReferenceSource {
  kind: string;
  name: string;
  path?: string | null;
  httpMethods?: string[] | null;
}

function normalizeAuthors(
  authors: DeveloperAuthorContribution[] | null | undefined,
): DeveloperAuthorContribution[] {
  return (authors ?? []).map((author) => ({
    name: author.name ?? '',
    initials: author.initials || '??',
    percent: Number(author.percent ?? 0),
    lines: Number(author.lines ?? 0),
  }));
}

function normalizeExternalServices(
  services: DeveloperExternalServiceInfo[] | null | undefined,
): CatalogExternalService[] {
  return (services ?? [])
    .map((service) => {
      const namingIssues = [...(service.namingIssues ?? [])];
      const namingCompliant = service.namingCompliant !== false && namingIssues.length === 0;
      const descriptions = normalizeCatalogDescriptions(service);
      return {
        name: service.name ?? '',
        project: service.project ?? '',
        entryPoint: service.entryPoint ?? null,
        kind: service.kind ?? null,
        namingIssues,
        namingCompliant,
        description: service.description ?? descriptions['en-US'] ?? '',
        descriptions,
      };
    })
    .filter((service) => !!service.name);
}

function normalizeConfigurationSections(
  sections: CatalogConfigurationSection[] | null | undefined,
): CatalogConfigurationSection[] {
  return (sections ?? [])
    .map((section) => ({
      section: section.section ?? '',
      optionsType: section.optionsType ?? null,
      source: section.source ?? null,
      currentConfigJson: section.currentConfigJson ?? null,
    }))
    .filter((section) => !!section.section);
}

function normalizeCatalogDescriptions(
  entity: {
    description?: string | null;
    descriptions?: Record<string, string> | null;
  },
): Record<string, string> {
  const fromMap = entity.descriptions;
  if (fromMap && typeof fromMap === 'object' && !Array.isArray(fromMap)) {
    const normalized: Record<string, string> = {};
    for (const [locale, value] of Object.entries(fromMap)) {
      if (typeof value === 'string' && value.trim()) {
        normalized[locale] = value.trim();
      }
    }
    if (Object.keys(normalized).length) {
      return normalized;
    }
  }

  const english = (entity.description ?? '').trim();
  return english ? { 'en-US': english } : {};
}

export function isExternalServiceNamingCompliant(
  service: CatalogExternalService | null | undefined,
): boolean {
  if (!service) {
    return true;
  }
  if (service.namingCompliant === false) {
    return false;
  }
  return !(service.namingIssues && service.namingIssues.length > 0);
}

export function externalServiceNamingIssuesTooltip(
  service: CatalogExternalService | null | undefined,
): string {
  const issues = service?.namingIssues ?? [];
  if (!issues.length) {
    return 'Entry-point naming looks fine';
  }
  return issues.join('\n');
}

export function externalServicesTooltip(
  services: CatalogExternalService[] | null | undefined,
): string {
  const list = services ?? [];
  if (!list.length) {
    return 'No external services detected';
  }
  return list
    .map((service) => {
      const entry = service.entryPoint ? ` · ${service.entryPoint}` : '';
      const project = service.project || 'unknown';
      const naming = isExternalServiceNamingCompliant(service) ? '' : ' · naming issue';
      return `${service.name} (${project}${entry}${naming})`;
    })
    .join('\n');
}

export function isCqrsCompliant(command: CatalogCommand | null | undefined): boolean {
  if (!command) {
    return true;
  }
  if (command.cqrsCompliant === false) {
    return false;
  }
  return !(command.cqrsIssues && command.cqrsIssues.length > 0);
}

export function cqrsIssuesTooltip(command: CatalogCommand | null | undefined): string {
  const issues = command?.cqrsIssues ?? [];
  if (!issues.length) {
    return 'Follows CQRS Command/Query naming';
  }
  return issues.join('\n');
}

export function isPlacementCompliant(command: CatalogCommand | null | undefined): boolean {
  if (!command) {
    return true;
  }
  if (command.placementCompliant === false) {
    return false;
  }
  return !(command.placementIssues && command.placementIssues.length > 0);
}

export function placementIssuesTooltip(command: CatalogCommand | null | undefined): string {
  const issues = command?.placementIssues ?? [];
  if (!issues.length) {
    return 'Command lives in the expected project';
  }
  return issues.join('\n');
}

/** Jobs, controllers, and controller actions share these orchestration flags. */
export type OrchestrationComplianceFields = Pick<
  CatalogJob | CatalogController | CatalogControllerAction,
  'orchestrationCompliant' | 'orchestrationIssues'
>;

export function isOrchestrationCompliant(
  entry: OrchestrationComplianceFields | null | undefined,
): boolean {
  if (!entry) {
    return true;
  }
  if (entry.orchestrationCompliant === false) {
    return false;
  }
  return !(entry.orchestrationIssues && entry.orchestrationIssues.length > 0);
}

export function orchestrationIssuesTooltip(
  entry: OrchestrationComplianceFields | null | undefined,
): string {
  const issues = entry?.orchestrationIssues ?? [];
  if (!issues.length) {
    return 'Thin orchestration: mostly MediatR Send';
  }
  return issues.join('\n');
}

export function actionCqrsIssuesTooltip(action: CatalogControllerAction | null | undefined): string {
  const issues = action?.cqrsIssues ?? [];
  if (!issues.length) {
    return 'HTTP verb and action name look consistent';
  }
  return issues.join('\n');
}

export function normalizeDeveloperCatalog(catalog: DeveloperCatalogDto): CatalogDto {
  const commands: CatalogCommand[] = (catalog.commands ?? []).map((command) => {
    const descriptions = normalizeCatalogDescriptions(command);
    return {
    ...command,
    name: command.name ?? '',
    fullName: command.fullName ?? '',
    kind: command.kind ?? '',
    description: command.description ?? descriptions['en-US'] ?? '',
    descriptions,
    group: command.group ?? 'Other',
    responseType: command.responseType ?? 'Unit',
    sourcePath: command.sourcePath ?? '',
    parameters: (command.parameters ?? []).map((parameter) => ({
      ...parameter,
      name: parameter.name ?? '',
      type: parameter.type ?? '',
      typeKind: parameter.typeKind ?? 'unknown',
      elementType: parameter.elementType ?? null,
      properties: parameter.properties ?? [],
    })),
    responseTypeShape: command.responseTypeShape
      ? {
          name: command.responseTypeShape.name ?? command.responseType ?? 'Unit',
          kind: command.responseTypeShape.kind ?? 'unknown',
          elementType: command.responseTypeShape.elementType ?? null,
          properties: command.responseTypeShape.properties ?? [],
        }
      : {
          name: command.responseType ?? 'Unit',
          kind: 'unknown',
          elementType: null,
          properties: [],
        },
    callers: command.callers ?? [],
    usedByJobs: command.usedByJobs ?? [],
    chainedCommands: command.chainedCommands ?? [],
    referencedByCommands: command.referencedByCommands ?? [],
    referenceCount:
      command.referenceCount
      ?? (command.callers?.length ?? 0)
        + (command.usedByJobs?.length ?? 0)
        + (command.referencedByCommands?.length ?? 0),
    authors: normalizeAuthors(command.authors),
    externalServices: normalizeExternalServices(command.externalServices),
    configurationSections: normalizeConfigurationSections(
      (command as CatalogCommand).configurationSections,
    ),
    discoveredAtRuntime: command.discoveredAtRuntime ?? false,
    executionCount: Number(command.executionCount ?? 0),
    lastExecutedAt: command.lastExecutedAt ?? null,
    cqrsCompliant: command.cqrsCompliant ?? true,
    cqrsIssues: [...(command.cqrsIssues ?? [])],
    placementCompliant: command.placementCompliant ?? true,
    placementIssues: [...(command.placementIssues ?? [])],
    obsolete: isCatalogItemObsolete(command),
  };
  }).map((command) => ({
    ...command,
    group: command.obsolete ? catalogArchiveGroup(command.group) : command.group,
  }));

  const jobs: CatalogJob[] = (catalog.jobs ?? []).map((job) => {
    const referencedCommands = job.referencedCommands?.length
      ? job.referencedCommands
      : job.usedCommands ?? [];
    const descriptions = normalizeCatalogDescriptions(job);
    const obsolete = isCatalogItemObsolete(job);
    return {
      ...job,
      name: job.name ?? '',
      description: job.description ?? descriptions['en-US'] ?? '',
      descriptions,
      group: obsolete ? catalogArchiveGroup(job.group ?? 'Other') : job.group ?? 'Other',
      sourcePath: job.sourcePath ?? '',
      registrationStatus: job.registrationStatus ?? 'NotRegistered',
      usedCommands: job.usedCommands ?? [],
      referencedCommands,
      referenceCount: job.referenceCount ?? referencedCommands.length,
      runtimeStatus: job.runtimeStatus ?? 'Unused',
      isScheduled: job.isScheduled ?? false,
      commentedOut: job.commentedOut ?? false,
      authors: normalizeAuthors(job.authors),
      externalServices: normalizeExternalServices(job.externalServices),
      configurationSections: normalizeConfigurationSections(
        (job as CatalogJob).configurationSections,
      ),
      discoveredAtRuntime: job.discoveredAtRuntime ?? false,
      executionCount: Number(job.executionCount ?? 0),
      lastExecutedAt: job.lastExecutedAt ?? null,
      orchestrationCompliant: job.orchestrationCompliant ?? true,
      orchestrationIssues: [...(job.orchestrationIssues ?? [])],
      processMigrationStatus: resolveJobProcessMigrationStatus({
        name: job.name,
        processMigrationStatus: (job as CatalogJob).processMigrationStatus,
      }),
      obsolete,
    };
  });

  const controllers: CatalogController[] = (catalog.controllers ?? []).map((controller) => {
    const actions = (controller.actions ?? []).map((action) => ({
      ...action,
      name: action.name ?? '',
      httpMethods: action.httpMethods ?? [],
      route: action.route ?? null,
      kind: action.kind ?? 'Command',
      usedCommands: action.usedCommands ?? [],
      orchestrationCompliant: action.orchestrationCompliant ?? true,
      orchestrationIssues: [...(action.orchestrationIssues ?? [])],
      cqrsCompliant: action.cqrsCompliant ?? true,
      cqrsIssues: [...(action.cqrsIssues ?? [])],
    }));
    const descriptions = normalizeCatalogDescriptions(controller);
    return {
      ...controller,
      name: controller.name ?? '',
      fullName: controller.fullName ?? '',
      description: controller.description ?? descriptions['en-US'] ?? '',
      descriptions,
      group: controller.group ?? 'Other',
      sourcePath: controller.sourcePath ?? '',
      route: controller.route ?? null,
      authors: normalizeAuthors(controller.authors),
      actions,
      orchestrationCompliant: controller.orchestrationCompliant ?? actions.every((action) => action.orchestrationCompliant),
      orchestrationIssues: [...(controller.orchestrationIssues ?? [])],
      cqrsIssueCount:
        controller.cqrsIssueCount
        ?? actions.filter((action) => action.cqrsCompliant === false).length,
      orchestrationIssueCount:
        controller.orchestrationIssueCount
        ?? actions.filter((action) => action.orchestrationCompliant === false).length,
    };
  });

  return {
    ...catalog,
    generatedBy: catalog.generatedBy ?? '',
    startJobs: catalog.startJobs ?? false,
    commands,
    jobs,
    controllers,
  };
}

export const CODE_CATALOG_URL = '/admin/orchestration/code-catalog';

export function developerCatalogCommandLink(name: string): string[] {
  return [CODE_CATALOG_URL, 'commands', name];
}

export function developerCatalogJobLink(name: string): string[] {
  return [CODE_CATALOG_URL, 'jobs', name];
}

/** Same-page Controllers tab selection via URL fragment. */
export function developerCatalogControllerFragment(name: string, group?: string | null): string {
  const state: Record<string, string> = {
    tab: 'Controllers',
    controller: name,
  };
  if (group) {
    state['group'] = group;
  }
  return Object.entries(state)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
}

export function developerCatalogControllerLink(): string[] {
  return [CODE_CATALOG_URL];
}

export function commandReferenceCount(command: CatalogCommand): number {
  return command.referenceCount
    ?? (command.callers?.length ?? 0)
      + (command.usedByJobs?.length ?? 0)
      + (command.referencedByCommands?.length ?? 0);
}

export function jobReferenceCount(job: CatalogJob): number {
  return job.referenceCount
    ?? (job.referencedCommands?.length || job.usedCommands?.length || 0);
}

export function commandReferenceSources(command: CatalogCommand): CatalogReferenceSource[] {
  const sources: CatalogReferenceSource[] = [];
  for (const caller of command.callers ?? []) {
    sources.push({
      kind: caller.kind || 'Other',
      name: caller.name || '',
      path: caller.path,
      httpMethods: caller.httpMethods,
    });
  }
  for (const job of command.usedByJobs ?? []) {
    if (!sources.some((source) => source.kind === 'Job' && source.name === job)) {
      sources.push({ kind: 'Job', name: job });
    }
  }
  for (const parent of command.referencedByCommands ?? []) {
    sources.push({ kind: 'Command', name: parent });
  }
  return sources;
}

export function runtimeSeverity(
  status: string | null | undefined,
): 'success' | 'warn' | 'danger' | 'secondary' | 'info' {
  switch (status) {
    case 'Enabled':
      return 'success';
    case 'DisabledByFlag':
      return 'warn';
    case 'JobsMasterOff':
      return 'danger';
    case 'Unused':
      return 'secondary';
    default:
      return 'info';
  }
}

export function isJobCaller(caller: DeveloperCommandCallerInfo): boolean {
  return caller.kind === 'Job';
}

export function formatExecutionSummary(
  count: number | null | undefined,
  lastExecutedAt: string | Date | null | undefined,
  now: Date = new Date(),
): string {
  const runs = Number(count ?? 0);
  if (runs <= 0 || !lastExecutedAt) {
    return runs > 0 ? String(runs) : '—';
  }

  return `${runs} · ${formatRelativeUtc(lastExecutedAt, now)}`;
}

export function formatExecutionTooltip(
  count: number | null | undefined,
  lastExecutedAt: string | Date | null | undefined,
): string {
  const runs = Number(count ?? 0);
  if (runs <= 0) {
    return 'No monitored executions yet';
  }

  if (!lastExecutedAt) {
    return `${runs} monitored execution${runs === 1 ? '' : 's'}`;
  }

  const absolute = new Date(lastExecutedAt).toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC');
  return `${runs} monitored execution${runs === 1 ? '' : 's'}\nLast: ${absolute}`;
}

function formatRelativeUtc(value: string | Date, now: Date): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  const deltaSeconds = Math.round((now.getTime() - date.getTime()) / 1000);
  if (deltaSeconds < 60) {
    return 'just now';
  }

  const minutes = Math.round(deltaSeconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.round(minutes / 60);
  if (hours < 48) {
    return `${hours}h ago`;
  }

  const days = Math.round(hours / 24);
  if (days < 14) {
    return `${days}d ago`;
  }

  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${day}.${month}`;
}

export function buildCatalogAuthorProfiles(catalog: CatalogDto | null | undefined): CatalogAuthorProfile[] {
  type Acc = {
    name: string;
    initials: string;
    participations: CatalogAuthorParticipation[];
  };

  const byName = new Map<string, Acc>();

  const addParticipation = (
    authors: DeveloperAuthorContribution[] | null | undefined,
    itemKind: CatalogAuthorParticipationKind,
    name: string,
    group: string,
  ): void => {
    for (const author of authors ?? []) {
      const authorName = (author.name ?? '').trim();
      if (!authorName) {
        continue;
      }
      const key = authorName.toLowerCase();
      let profile = byName.get(key);
      if (!profile) {
        profile = {
          name: authorName,
          initials: author.initials || '??',
          participations: [],
        };
        byName.set(key, profile);
      } else if (!profile.initials || profile.initials === '??') {
        profile.initials = author.initials || profile.initials;
      }

      profile.participations.push({
        itemKind,
        name,
        group: group || 'Other',
        percent: Number(author.percent ?? 0),
        lines: Number(author.lines ?? 0),
      });
    }
  };

  for (const command of catalog?.commands ?? []) {
    const kind = (command.kind as CatalogMediatorKind) || 'Command';
    addParticipation(command.authors, kind, command.name || '', command.group || 'Other');
  }

  for (const job of catalog?.jobs ?? []) {
    addParticipation(job.authors, 'Job', job.name || '', job.group || 'Other');
  }

  for (const controller of catalog?.controllers ?? []) {
    addParticipation(
      controller.authors,
      'Controller',
      controller.name || '',
      controller.group || 'Other',
    );
  }

  return [...byName.values()]
    .map((profile) => {
      const participations = [...profile.participations].sort(compareCatalogAuthorParticipations);
      const commandCount = participations.filter(
        (item) =>
          item.itemKind === 'Command'
          || item.itemKind === 'Query'
          || item.itemKind === 'Request'
          || item.itemKind === 'CliCommand',
      ).length;
      const jobCount = participations.filter((item) => item.itemKind === 'Job').length;
      const controllerCount = participations.filter(
        (item) => item.itemKind === 'Controller',
      ).length;
      const avgPercent = participations.length
        ? Math.round(
            (participations.reduce((sum, item) => sum + item.percent, 0) / participations.length) * 10,
          ) / 10
        : 0;
      return {
        name: profile.name,
        initials: profile.initials,
        itemCount: participations.length,
        commandCount,
        jobCount,
        controllerCount,
        avgPercent,
        participations,
      };
    })
    .sort(
      (left, right) =>
        right.itemCount - left.itemCount
        || right.avgPercent - left.avgPercent
        || left.name.localeCompare(right.name),
    );
}

export function buildCatalogServiceProfiles(
  catalog: CatalogDto | null | undefined,
): CatalogServiceProfile[] {
  type Acc = {
    name: string;
    project: string;
    description: string;
    descriptions: Record<string, string>;
    consumers: CatalogServiceConsumer[];
  };

  const byKey = new Map<string, Acc>();

  const addConsumer = (
    services: CatalogExternalService[] | null | undefined,
    itemKind: CatalogAuthorParticipationKind,
    name: string,
    group: string,
  ): void => {
    for (const service of services ?? []) {
      const serviceName = (service.name ?? '').trim();
      if (!serviceName) {
        continue;
      }
      const project = service.project || serviceName;
      const key = `${serviceName.toLowerCase()}|${project.toLowerCase()}`;
      let profile = byKey.get(key);
      if (!profile) {
        profile = {
          name: serviceName,
          project,
          description: service.description
            ?? normalizeCatalogDescriptions(service)['en-US']
            ?? '',
          descriptions: normalizeCatalogDescriptions(service),
          consumers: [],
        };
        byKey.set(key, profile);
      } else if (!profile.description) {
        const descriptions = normalizeCatalogDescriptions(service);
        const english = service.description ?? descriptions['en-US'] ?? '';
        if (english) {
          profile.description = english;
          profile.descriptions = descriptions;
        }
      }
      profile.consumers.push({
        itemKind,
        name,
        group: group || 'Other',
        entryPoint: service.entryPoint ?? null,
        kind: service.kind ?? null,
        namingIssues: [...(service.namingIssues ?? [])],
        namingCompliant: isExternalServiceNamingCompliant(service),
      });
    }
  };

  for (const command of catalog?.commands ?? []) {
    const kind = (command.kind as CatalogMediatorKind) || 'Command';
    addConsumer(command.externalServices, kind, command.name || '', command.group || 'Other');
  }

  for (const job of catalog?.jobs ?? []) {
    addConsumer(job.externalServices, 'Job', job.name || '', job.group || 'Other');
  }

  return [...byKey.values()]
    .map((profile) => {
      const consumers = [...profile.consumers].sort(
        (left, right) =>
          left.itemKind.localeCompare(right.itemKind) || left.name.localeCompare(right.name),
      );
      const commandCount = consumers.filter((item) => item.itemKind !== 'Job').length;
      const jobCount = consumers.filter((item) => item.itemKind === 'Job').length;
      const namingIssues = [
        ...new Set(
          consumers.flatMap((item) => item.namingIssues ?? []).filter((issue) => !!issue),
        ),
      ];
      return {
        name: profile.name,
        project: profile.project,
        description: profile.description,
        descriptions: profile.descriptions,
        itemCount: consumers.length,
        commandCount,
        jobCount,
        namingIssues,
        namingCompliant: namingIssues.length === 0,
        consumers,
      };
    })
    .sort(
      (left, right) =>
        right.itemCount - left.itemCount || left.name.localeCompare(right.name),
    );
}
